import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';
import { useQuickSelectItems } from '@/hooks/useQuickSelectItems';

/**
 * "Quick select date range" dropdown. Builds on `useQuickSelectItems` which
 * aggregates four groups (latest data, tracking periods, past reports, CGM
 * devices) into a single list. Each group is hidden when empty so the
 * dropdown shape adapts to the patient — a fresh patient may only show
 * "Latest data"; a long-term patient shows all four.
 *
 * Pure presentational over the hook — no fetching of its own. Renders
 * nothing when no group has items, matching v1's behaviour
 * (clinician/src/components/dateRangeFilter.tsx:18) where the dropdown
 * silently disappeared if there was nothing to pick.
 */
type Props = {
  patientId: number | null | undefined;
  onPick: (range: { from: string; to: string }) => void;
  /** Override the trigger label. Defaults to "Quick select date range". */
  triggerLabel?: string;
  className?: string;
};

export const QuickSelectRange = ({ patientId, onPick, triggerLabel, className }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Once the user opens the dropdown, we leave heavy queries enabled even
  // after they close it so re-opening is instant. The first open pays the
  // cost; subsequent opens read from cache.
  const [hasOpened, setHasOpened] = useState(false);
  const { groups, isEmpty } = useQuickSelectItems(patientId, { enabled: hasOpened });

  // The dropdown can render without the heavy queries — we just show
  // whatever groups are available (typically just "Latest data" via the
  // cheap useLastMealDate). Don't bail on `isEmpty` until everything has
  // loaded; otherwise the trigger hides on first paint and never returns.
  const shouldRender = !isEmpty || !hasOpened;
  if (!shouldRender) return null;

  const handlePick = (range: { from: string; to: string }) => {
    onPick(range);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // First open flips the latch — keeps heavy queries enabled from now
        // on so the user doesn't see a loading spinner on every re-open.
        if (next) setHasOpened(true);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('justify-between', className)}>
          <span className="truncate">{triggerLabel ?? t('Quick select date range')}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* `w-80` is the ideal width; `max-w-[var(...available-width)]` lets
          Radix shrink the popover when the trigger sits near a viewport
          edge (narrow viewport with sidebar + patient rail open). Without
          this cap the 320px popover spills past the card and clips
          against the rail. `collisionPadding` keeps an 8px gutter. */}
      <PopoverContent
        className="w-80 max-w-[var(--radix-popover-content-available-width)] p-0"
        align="start"
        collisionPadding={8}
      >
        <div className="max-h-80 overflow-y-auto">
          {groups.map((group) => (
            <div key={group.id}>
              <div className="border-b bg-muted/50 px-3 py-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </div>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handlePick({ from: item.from, to: item.to })}
                  className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted"
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
